import { OnlyFansSubscriber } from '../../../../hooks/useSubscriber';
import { OnlyFansVaultMedias } from '../../../../hooks/useSubscriberVaultAlbums';
import {
    OnlyFansListIdAndName,
    OnlyFansSubscriberMessageUsersAutoMessageDetails,
    OnlyFansSubscriberMessageUsersTtsSettings,
} from '../../../../types/messageUsersAutoSendWelcomeMessage';

export interface ValidationDependencies {
    messageDetails: OnlyFansSubscriberMessageUsersAutoMessageDetails;
    includeMedia: boolean;
    medias: OnlyFansVaultMedias.RestructuredResponseItem[];
    lockMessage: boolean;
    tagCreators: boolean;
    taggedCreators: { id: number; name: string }[];
    taggedReleaseForms: { id: number; name: string }[];
    addToList: boolean;
    addToListIdAndName: OnlyFansListIdAndName | undefined | null;
    excludeUserInLists: boolean;
    excludeLists: OnlyFansListIdAndName[];
    ttsSettings: OnlyFansSubscriberMessageUsersTtsSettings;
    subscriber: OnlyFansSubscriber;
}

/**
 * Validates the form state and returns whether the form is valid
 * @param deps - All dependencies needed for validation
 * @returns true if the form is valid, false otherwise
 */
export const validateForm = (deps: ValidationDependencies): boolean => {
    const {
        messageDetails,
        includeMedia,
        medias,
        lockMessage,
        tagCreators,
        taggedCreators,
        taggedReleaseForms,
        addToList,
        addToListIdAndName,
        excludeUserInLists,
        excludeLists,
        ttsSettings,
        subscriber,
    } = deps;

    // If the message is not active, form is valid (disabled state)
    if (!messageDetails?.active) {
        return true;
    }

    // Validate message content
    if (!validateMessage(messageDetails.message)) {
        return false;
    }

    // Validate media
    if (!validateMedia(includeMedia, medias)) {
        return false;
    }

    // Validate price lock
    if (!validatePriceLock(lockMessage, messageDetails.lockMessagePrice)) {
        return false;
    }

    // Validate tagged creators
    if (!validateTaggedCreators(tagCreators, taggedCreators, taggedReleaseForms)) {
        return false;
    }

    // Validate add to list
    if (!validateAddToList(addToList, addToListIdAndName)) {
        return false;
    }

    // Validate exclude lists
    if (!validateExcludeLists(excludeUserInLists, excludeLists)) {
        return false;
    }

    // Validate TTS settings
    if (!validateTtsSettings(ttsSettings, subscriber)) {
        return false;
    }

    return true;
};

/**
 * Validates that the message is not empty
 */
const validateMessage = (message: string): boolean => {
    return message.trim().length > 0;
};

/**
 * Validates media requirements
 */
const validateMedia = (includeMedia: boolean, medias: OnlyFansVaultMedias.RestructuredResponseItem[]): boolean => {
    if (includeMedia && medias.length === 0) {
        return false;
    }
    return true;
};

/**
 * Validates price lock settings
 */
const validatePriceLock = (lockMessage: boolean, lockMessagePrice: number | null): boolean => {
    if (lockMessage && (lockMessagePrice === null || lockMessagePrice === 0)) {
        return false;
    }
    return true;
};

/**
 * Validates tagged creators settings
 */
const validateTaggedCreators = (
    tagCreators: boolean,
    taggedCreators: { id: number; name: string }[],
    taggedReleaseForms: { id: number; name: string }[],
): boolean => {
    if (tagCreators && taggedCreators.length === 0 && taggedReleaseForms.length === 0) {
        return false;
    }
    return true;
};

/**
 * Validates add to list settings
 */
const validateAddToList = (addToList: boolean, addToListIdAndName: OnlyFansListIdAndName | undefined | null): boolean => {
    if (addToList && !addToListIdAndName) {
        return false;
    }
    return true;
};

/**
 * Validates exclude lists settings
 */
const validateExcludeLists = (excludeUserInLists: boolean, excludeLists: OnlyFansListIdAndName[]): boolean => {
    if (excludeUserInLists && excludeLists.length === 0) {
        return false;
    }
    return true;
};

/**
 * Validates TTS settings
 */
const validateTtsSettings = (ttsSettings: OnlyFansSubscriberMessageUsersTtsSettings, subscriber: OnlyFansSubscriber): boolean => {
    if (!ttsSettings?.active) {
        return true;
    }

    // Validate model ID
    if (!ttsSettings.modelId || ttsSettings.modelId.trim().length === 0) {
        return false;
    }

    // Validate username greeting if addUsername is enabled
    if (ttsSettings.addUsername && (!ttsSettings.usernameGreeting || ttsSettings.usernameGreeting.trim().length === 0)) {
        return false;
    }

    // Validate message
    if (!ttsSettings.message || ttsSettings.message.trim().length === 0) {
        return false;
    }

    // Validate generated filename
    if (!ttsSettings.generatedFilename) {
        return false;
    }

    // Validate ElevenLabs API key
    if (ttsSettings.provider === 'elevenlabs' && !subscriber.messageUsers?.elevenLabsApiKey) {
        return false;
    }

    return true;
};
